import { Component, ComponentInterface, Element, h, Host, JSX, State } from '@stencil/core';
import { DebounceDelays } from '../../shared/debounce-constants';
import { throttle } from '../../utils/throttle';
import { VerticalScrollMaskPosition } from './vertical-scroll.types';
import { MASK_POSITION_BOTTOM, MASK_POSITION_TOP } from './vertical-scroll.constants';

/**
 * @slot - Contents of vertical-scroll
 **/
@Component({
  tag: 'wb-vertical-scroll',
  styleUrl: './vertical-scroll.scss',
  shadow: true,
})
export class VerticalScrollComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  @State()
  private isScrolledToTop: boolean = true;

  @State()
  private isScrolledToBottom: boolean = true;

  @State()
  private maskPosition: VerticalScrollMaskPosition = MASK_POSITION_BOTTOM;

  private innerEl: HTMLDivElement;
  private mutationObserver: MutationObserver;

  public componentDidLoad(): void {
    this.mutationObserver = new MutationObserver(this.update);
    this.mutationObserver.observe(this.innerEl, { childList: true, subtree: true });
    this.update();
  }

  public disconnectedCallback(): void {
    this.mutationObserver.disconnect();
  }

  private bindInnerEl = (el: HTMLDivElement) => {
    this.innerEl = el;
  };

  private update = (): void => {
    if (!this.innerEl) {
      return;
    }

    const prevIsScrolledToTop = this.isScrolledToTop;
    const prevIsScrolledToBottom = this.isScrolledToBottom;

    this.isScrolledToTop = this.innerEl.scrollTop === 0;
    this.isScrolledToBottom = this.innerEl.scrollTop === this.innerEl.scrollHeight - this.innerEl.clientHeight;

    if (!prevIsScrolledToTop && this.isScrolledToTop) {
      this.maskPosition = MASK_POSITION_BOTTOM;
    }
    if (!prevIsScrolledToBottom && this.isScrolledToBottom) {
      this.maskPosition = MASK_POSITION_TOP;
    }
  };

  private throttledUpdate: () => void = throttle(() => this.update(), DebounceDelays.Scroll);

  public render(): JSX.Element {
    return (
      <Host class="wb-vertical-scroll">
        <div
          class={{
            container: true,
            'has-mask': !this.isScrolledToTop || !this.isScrolledToBottom,
            [`mask-${this.maskPosition}`]: true,
            'mask-full': !this.isScrolledToTop && !this.isScrolledToBottom,
          }}
        >
          <div class="inner" onScroll={this.throttledUpdate} ref={this.bindInnerEl}>
            <slot />
          </div>
        </div>
      </Host>
    );
  }
}
